import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/hooks/useAuth";
import { OrganizationProvider } from "@/hooks/useOrganization";
import ProtectedRoute from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import Connect from "./pages/Connect";
import NewLoad from "./pages/NewLoad";
import Carriers from "./pages/Carriers";
import PublicProfile from "./pages/PublicProfile";
import UserSearch from "./pages/UserSearch";
import LoadOrderTracking from "./pages/LoadOrderTracking";
import CustomerProfile from "./pages/CustomerProfile";
import CarrierDashboardPage from "./pages/CarrierDashboard";
import CustomerDashboardPage from "./pages/CustomerDashboard";
import "./i18n";
import ErrorBoundary from "./components/ErrorBoundary";
import * as Sentry from "@sentry/react";
import RouteChangeTracker from "./components/analytics/RouteChangeTracker";
import { HelmetProvider } from "react-helmet-async";
import ArIndex from "./pages/ArIndex";

const queryClient = new QueryClient();
const SentryRoutes = Sentry.withSentryRouting(Routes);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <OrganizationProvider>
        <LanguageProvider>
          <HelmetProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <RouteChangeTracker />
                <SentryRoutes>
                <Route path="/" element={<Index />} />
                <Route path="/ar" element={<ArIndex />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/u/:username" element={<PublicProfile />} />
                <Route path="/load/:id" element={<LoadOrderTracking />} />
                <Route 
                  path="/search" 
                  element={
                    <ProtectedRoute>
                      <UserSearch />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/connect" 
                  element={<Connect />} 
                />
                <Route 
                  path="/new-load" 
                  element={
                    <ProtectedRoute>
                      <NewLoad />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/carriers" 
                  element={
                    <ProtectedRoute>
                      <Carriers />
                    </ProtectedRoute>
                  } 
                />
                {/* Dashboard routes */}
                <Route 
                  path="/carrier-dashboard" 
                  element={
                    <ProtectedRoute>
                      <CarrierDashboardPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/customer-dashboard" 
                  element={
                    <ProtectedRoute>
                      <CustomerDashboardPage />
                    </ProtectedRoute>
                  } 
                />
                {/* Customer profile route - must be last before catch-all */}
                <Route path="/:username" element={<CustomerProfile />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </SentryRoutes>
            </BrowserRouter>
          </TooltipProvider>
          </HelmetProvider>
        </LanguageProvider>
      </OrganizationProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
